/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */

import Vue from 'vue';
import routeListService from '@/modules/Flexigo/services/routeList';
import stopsService from '@/modules/Flexigo/services/stopsService';
import {
  mappedRouteData,
  mappedRouteDataFromRouteDetails,
  getRouteCoordinates,
  getDirectionsHere,
  completeRouteSummary,
  getUpdatedRouteDetails,
  mappedStationData,
  getRouteTimeParams,
} from '../../components/routeList/utils';
import { categoryPinColor, mergeCategories } from '../../constants/stopsCategories';

/**
 * VSER-13369: exact-coordinate key for station↔stop matching. The BE canonical
 * resolver (`VTFStationCanonicalResolver.findByExactCoordinates`) reuses an
 * existing station row on exact double equality, so the FE mirrors that rule.
 */
function coordKey(location) {
  if (!location || location.latitude == null || location.longitude == null) return null;
  return `${location.latitude},${location.longitude}`;
}

/**
 * Returns true when `outer` bbox fully contains `inner` bbox.
 * Each bbox is { minLat, maxLat, minLng, maxLng }.
 */
function isBboxCovering(outer, inner) {
  if (!outer || !inner) return false;
  return (
    outer.minLat <= inner.minLat &&
    outer.maxLat >= inner.maxLat &&
    outer.minLng <= inner.minLng &&
    outer.maxLng >= inner.maxLng
  );
}

/**
 * Filters a list of stops to those whose location falls within the given bbox.
 */
function filterStopsByBbox(stops, bbox) {
  if (!Array.isArray(stops) || !bbox) return [];
  return stops.filter((stop) => {
    const { latitude, longitude } = stop?.location || {};
    return (
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      latitude >= bbox.minLat &&
      latitude <= bbox.maxLat &&
      longitude >= bbox.minLng &&
      longitude <= bbox.maxLng
    );
  });
}

export const routeActions = {
  VIEW: 'view',
  EDIT: 'edit',
  SAVE: 'save',
  SAVE_AND_ACTIVATE: 'saveAndActivate',
  ASSIGN_PERSONNEL: 'assignPersonnel',
  TRANSFER_ROUTE: 'transferRoute',
  CANCEL: 'cancel',
  CLOSE_AND_REFRESH: 'closeAndRefresh',
};

export default {
  state: {
    routesData: { TO: {}, FROM: {} },
    isRouteToggleOn: true, // TO or FROM, TO is the default
    displayFirstCalculationMessage: { TO: false, FROM: false, MULTI_SHIFTS: false },
    isCalculated: { TO: false, FROM: false },
    isLoading: false,
    routeEditActionMode: routeActions.VIEW,
    shouldCalculateTime: true, // Controls whether to calculate stop times
    showMissingTimesWarning: false, // Shows warning when calculate is clicked without time calculation
    routeVersionSource: 'active', // 'draft' | 'active' - tracks which version is currently active
    storedRouteVersion: {
      activeVersion: null,
      draftVersion: null,
      activeStations: [],
      draftStations: [],
    },
    cardReadData: null,
    isDragging: false,
    legendItems: {},
    routeDetails: null,
    routeHistoryMarker: [],
    // VSER-13369: system-registered stops shown on the route map (capped client-side
    // load — see systemStopsMarkers.vue). `stopsById` indexes loaded + route-added
    // stops by POI id so route pins can be coloured by their category.
    stopsOnMap: [],
    stopsById: {},
    stopsTruncated: false,
    showStopsOnMap: false, // VSER-13369 feature A: "Show stops on map" toggle (edit map)
    showRestrictionsOnMap: false, // VSER-13713: street-restriction overlay toggle (detail + edit maps)
    stopCategories: [], // VSER-13369: merged stop categories (legend + picker), fetched once
    // VSER-13369: stationId -> { poiId, category } for stops added from a POI this
    // session. BE's POST /v3/stations does not persist/return vtspPoiId yet, so we
    // keep the link client-side to colour the route pin by category (§5.4).
    routeStationPoiById: {},
    // Bbox cache for GET /v3/stops. Both `indexStopsForStations` (pin-colouring) and
    // `loadStopsOnMap` (system-POI layer) hit the same endpoint with a bbox. When the
    // cached bbox covers the requested bbox AND the cache was not truncated, the
    // cached stops are filtered client-side instead of re-fetching. This eliminates
    // the duplicate request that fires when the direction is toggled or the editor
    // opens (station-extent query + viewport query overlap).
    stopsCache: { bbox: null, stops: [], truncated: false, categories: null },
  },

  mutations: {
    SET_SELECTED_WORKGROUP(state, payload) {
      Vue.set(state, 'selectedWorkgroup', payload);
    },

    SET_ROUTES_ALL(state, payload) {
      Vue.set(state, 'routesData', payload);
    },

    SET_ROUTES(state, { route, payload }) {
      Vue.set(state.routesData, route, payload);
    },

    UPDATE_ROUTES(state, { route, payload }) {
      const updatedRoute = { ...state.routesData[route], ...payload };
      Vue.set(state.routesData, route, updatedRoute);
    },

    RESET_ROUTES(state) {
      Vue.set(state, 'routesData', { TO: {}, FROM: {} });
    },
    SET_RETURN_TOGGLE(state, payload) {
      Vue.set(state, 'isRouteToggleOn', payload);
    },
    SET_DISPLAY_FIRST_CALCULATION_MESSAGE(state, { key, value }) {
      Vue.set(state.displayFirstCalculationMessage, key, value);
    },
    // A direction's derived geometry + station times are only consistent with its
    // current stop list right after a successful calculation. Any structural change
    // (add / mirror / remove / reorder) must invalidate it so the Save guard blocks
    // until the direction is recalculated. `calculateRouteAndStations` sets it back
    // to true. This is the single source of truth for the Save guard.
    SET_IS_CALCULATED(state, { route, value }) {
      Vue.set(state.isCalculated, route, value);
    },
    SET_ROUTE_EDIT_ACTION_MODE(state, payload) {
      Vue.set(state, 'routeEditActionMode', payload);

      // Reset isRouteToggleOn to true (TO direction) when exiting edit mode
      if (payload === 'view') {
        Vue.set(state, 'isRouteToggleOn', true);
        // VSER-13369 §5.1: "Show stops on map" must come up OFF for each editor session.
        Vue.set(state, 'showStopsOnMap', false);
        // Clear the stops bbox cache so the next edit session fetches fresh data.
        Vue.set(state, 'stopsCache', { bbox: null, stops: [], truncated: false, categories: null });
      }
    },
    SET_ROUTE_HISTORY_MARKER(state, payload) {
      Vue.set(state, 'routeHistoryMarker', payload);
    },
    SET_SHOULD_CALCULATE_TIME(state, payload) {
      Vue.set(state, 'shouldCalculateTime', payload);
    },
    SET_SHOW_MISSING_TIMES_WARNING(state, payload) {
      Vue.set(state, 'showMissingTimesWarning', payload);
    },
    SET_STORED_ACTIVE_VERSION(state, payload) {
      if (payload) {
        Vue.set(state.storedRouteVersion, 'activeVersion', payload.activeVersion || null);
        Vue.set(state.storedRouteVersion, 'activeStations', payload.stations || []);
      }
    },

    SET_STORED_ROUTE_VERSION(state, { versionType, payload }) {
      // versionType: 'active' or 'draft'
      // Add null check for payload to prevent runtime errors
      if (!payload) {
        console.warn('SET_STORED_ROUTE_VERSION called with null/undefined payload');
        return;
      }

      if (versionType === 'active') {
        // Payload structure: { activeVersion: {...}, stations: [...] }
        Vue.set(state.storedRouteVersion, 'activeVersion', payload.activeVersion || payload);
        Vue.set(state.storedRouteVersion, 'activeStations', payload.stations || []);
      } else if (versionType === 'draft') {
        // Payload structure: { draftVersion: {...}, stations: [...] }
        // If payload already has draftVersion property, use it; otherwise payload IS the draftVersion
        Vue.set(state.storedRouteVersion, 'draftVersion', payload.draftVersion || payload);
        Vue.set(state.storedRouteVersion, 'draftStations', payload.stations || []);
      }
    },

    RESET_STORED_ROUTE_VERSION(state) {
      Vue.set(state, 'storedRouteVersion', {
        activeVersion: null,
        draftVersion: null,
        activeStations: [],
        draftStations: [],
      });
      Vue.set(state, 'routeVersionSource', 'active');
      // Reset calculation status to prevent showing stale status for new version
      Vue.set(state, 'isCalculated', { TO: false, FROM: false });
      // Reset calculation message flags
      Vue.set(state, 'displayFirstCalculationMessage', { TO: false, FROM: false, MULTI_SHIFTS: false });
    },

    SET_CURRENT_VERSION_SOURCE(state, source) {
      // Validate source value
      if (source !== 'draft' && source !== 'active') {
        console.warn(`Invalid version source: ${source}. Must be 'draft' or 'active'. Defaulting to 'draft'.`);
        Vue.set(state, 'routeVersionSource', 'draft');
        return;
      }
      Vue.set(state, 'routeVersionSource', source);
    },
    SET_LOADING(state, payload) {
      Vue.set(state, 'isLoading', payload);
    },
    SET_CARD_READ_DATA(state, payload) {
      Vue.set(state, 'cardReadData', payload);
    },
    SET_DRAGGING(state, payload) {
      Vue.set(state, 'isDragging', payload);
    },
    SET_SINGLE_LEGEND_ITEM(state, { item, value }) {
      Vue.set(state.legendItems, item, value);
    },
    SET_LEGEND_ITEMS(state, payload) {
      Vue.set(state, 'legendItems', payload);
    },

    UPDATE_PERSONNEL_STATION_MAPPING(state, { route, personnelId, stationId }) {
      // Initialize routesData[route] if it doesn't exist
      if (!state.routesData[route]) {
        Vue.set(state.routesData, route, {
          personnelStations: {},
          personnelCount: 0,
        });
      }

      // Initialize personnelStations if it doesn't exist
      if (!state.routesData[route].personnelStations) {
        Vue.set(state.routesData[route], 'personnelStations', {});
      }

      const originalStation = state.routesData[route].personnelStations[personnelId];

      // Remove from original station mapping if exists
      if (originalStation) {
        Vue.delete(state.routesData[route].personnelStations, personnelId);
      }

      // Add to new station mapping
      Vue.set(state.routesData[route].personnelStations, personnelId, stationId);
    },

    REMOVE_PERSONNEL_STATION_MAPPING(state, { route, personnelId }) {
      // Remove personnel from station mapping when they are unassigned
      if (state.routesData[route]?.personnelStations?.[personnelId]) {
        Vue.delete(state.routesData[route].personnelStations, personnelId);
      }
    },
    SET_ROUTE_DETAILS(state, payload) {
      Vue.set(state, 'routeDetails', payload);
    },
    SET_STOPS_ON_MAP(state, items) {
      const list = Array.isArray(items) ? items : [];
      Vue.set(state, 'stopsOnMap', list);
      // Batch: merge all stops into stopsById in a single Vue.set to avoid
      // per-stop reactivity cascade (356 individual Vue.set calls took 12+ seconds).
      const merged = { ...state.stopsById };
      list.forEach((stop) => {
        if (stop && stop.id != null) merged[stop.id] = stop;
      });
      Vue.set(state, 'stopsById', merged);
    },
    INDEX_STOP(state, stop) {
      if (stop && stop.id != null) Vue.set(state.stopsById, stop.id, stop);
    },
    // Batch version of INDEX_STOP — merges all stops into stopsById in a single
    // Vue.set call. Use this instead of looping INDEX_STOP when indexing many stops.
    INDEX_STOPS_BULK(state, stops) {
      if (!Array.isArray(stops)) return;
      const merged = { ...state.stopsById };
      stops.forEach((stop) => {
        if (stop && stop.id != null) merged[stop.id] = stop;
      });
      Vue.set(state, 'stopsById', merged);
    },
    SET_STOPS_CACHE(state, { bbox, stops, truncated, categories }) {
      Vue.set(state, 'stopsCache', {
        bbox,
        stops: Array.isArray(stops) ? stops : [],
        truncated: truncated === true,
        categories: categories || null,
      });
    },
    RESET_STOPS_CACHE(state) {
      Vue.set(state, 'stopsCache', { bbox: null, stops: [], truncated: false, categories: null });
    },
    SET_STOPS_TRUNCATED(state, value) {
      Vue.set(state, 'stopsTruncated', value === true);
    },
    SET_SHOW_STOPS_ON_MAP(state, value) {
      Vue.set(state, 'showStopsOnMap', value === true);
    },
    SET_SHOW_RESTRICTIONS_ON_MAP(state, value) {
      Vue.set(state, 'showRestrictionsOnMap', value === true);
    },
    SET_ROUTE_STATION_POI(state, { stationId, poiId, category }) {
      if (stationId == null) return;
      Vue.set(state.routeStationPoiById, stationId, { poiId, category });
    },
    SET_STOP_CATEGORIES(state, categories) {
      Vue.set(state, 'stopCategories', Array.isArray(categories) ? categories : []);
    },
  },
  actions: {
    // VSER-13369: merged stop categories (BE labels/colours over FE defaults), cached.
    async loadStopCategories({ state, commit }) {
      if (state.stopCategories.length) return state.stopCategories;
      let merged;
      try {
        merged = mergeCategories(await stopsService.getCategories());
      } catch (err) {
        merged = mergeCategories([]);
      }
      const available = merged.filter((c) => c.available !== false);
      commit('SET_STOP_CATEGORIES', available);
      return available;
    },

    // VSER-13369: resolve categories for the route's stations so pins can be coloured
    // by category after a reload (§5.4). A stop IS a station row (+ metadata) on the
    // BE, and Add-to-Route reuses the canonical station on exact coordinates — so one
    // bbox query over the stations' extent indexes every stop-backed station by
    // id/coords. Manual (non-stop) stations aren't returned and stay black.
    // Uses the stops bbox cache — if the station extent is already covered by a
    // previous (non-truncated) fetch, the cached stops are filtered client-side
    // instead of re-hitting the API.
    async indexStopsForStations({ state, commit }, stations = []) {
      const coords = (stations || [])
        .map((s) => s && s.location)
        .filter((l) => l && Number.isFinite(l.latitude) && Number.isFinite(l.longitude));
      if (!coords.length) return;
      const pad = 0.0005;
      const bounds = {
        minLat: Math.min(...coords.map((l) => l.latitude)) - pad,
        maxLat: Math.max(...coords.map((l) => l.latitude)) + pad,
        minLng: Math.min(...coords.map((l) => l.longitude)) - pad,
        maxLng: Math.max(...coords.map((l) => l.longitude)) + pad,
      };

      // Cache hit: the cached bbox covers the station extent, was not truncated,
      // and was not category-filtered (this action needs all categories for pin colouring).
      const { stopsCache } = state;
      if (
        !stopsCache.truncated &&
        stopsCache.stops.length &&
        stopsCache.categories === null &&
        isBboxCovering(stopsCache.bbox, bounds)
      ) {
        commit('INDEX_STOPS_BULK', filterStopsByBbox(stopsCache.stops, bounds));
        return;
      }

      try {
        const { items = [], total = 0 } = await stopsService.getList({ ...bounds, limit: 500 });
        const truncated = total > items.length;
        commit('INDEX_STOPS_BULK', items);
        commit('SET_STOPS_CACHE', {
          bbox: bounds,
          stops: items,
          truncated,
          categories: null,
        });
      } catch (err) {
        console.error('indexStopsForStations failed', err);
      }
    },

    // VSER-13369: load system-registered stops for the current map viewport via the
    // BE bbox filter on GET /v3/stops (all four bbox params or none; limit 500, max
    // 1000). `truncated` is true when the viewport holds more stops than returned.
    // Uses the stops bbox cache — if the viewport is already covered by a previous
    // (non-truncated) fetch with the same categories, the cached stops are filtered
    // client-side instead of re-hitting the API.
    async loadStopsOnMap({ state, commit }, { categories = [], bounds = null } = {}) {
      const normalizedCategories = Array.isArray(categories) && categories.length > 0 ? categories : null;

      // Cache hit: the cached bbox covers the requested viewport, was not truncated,
      // and the categories match.
      if (bounds) {
        const { stopsCache } = state;
        const categoriesMatch =
          (stopsCache.categories === null && normalizedCategories === null) ||
          JSON.stringify(stopsCache.categories) === JSON.stringify(normalizedCategories);

        if (
          !stopsCache.truncated &&
          stopsCache.stops.length &&
          categoriesMatch &&
          isBboxCovering(stopsCache.bbox, bounds)
        ) {
          const filtered = filterStopsByBbox(stopsCache.stops, bounds);
          commit('SET_STOPS_ON_MAP', filtered);
          commit('SET_STOPS_TRUNCATED', false);
          return { items: filtered, truncated: false };
        }
      }

      try {
        const params = { categories, limit: 500 };
        if (bounds) {
          params.minLat = bounds.minLat;
          params.maxLat = bounds.maxLat;
          params.minLng = bounds.minLng;
          params.maxLng = bounds.maxLng;
        }
        const result = await stopsService.getList(params);
        const resultTruncated = (result.total || 0) > (result.items || []).length;
        commit('SET_STOPS_ON_MAP', result.items);
        commit('SET_STOPS_TRUNCATED', resultTruncated);
        // Only cache when a bbox was used (without bbox the BE returns all stops
        // regardless of viewport, which is not a bbox-cacheable result).
        if (bounds) {
          commit('SET_STOPS_CACHE', {
            bbox: bounds,
            stops: result.items || [],
            truncated: resultTruncated,
            categories: normalizedCategories,
          });
        }
        return result;
      } catch (err) {
        console.error('loadStopsOnMap failed', err);
        return { items: [], truncated: false };
      }
    },
    toggleDraftMode({ commit, state }) {
      const newSource = state.routeVersionSource === 'draft' ? 'active' : 'draft';
      commit('SET_CURRENT_VERSION_SOURCE', newSource);
    },

    // `shouldApply` (optional) is checked once the response arrives, before anything is committed;
    // returning false drops the response (resolves to null) so a stale refresh can't overwrite newer state.
    async fetchRouteVersion({ commit, dispatch, state }, { routeId, fetchActive = null, shouldApply = null }) {
      // Validate routeId is provided before making API call
      if (!routeId) {
        if (process.env.NODE_ENV === 'development') {
          console.error('fetchRouteVersion called without routeId');
        }
        throw new Error('Route ID is required to fetch route version');
      }

      // If fetchActive is not explicitly set, use current version source from state
      let shouldFetchActive = fetchActive;
      if (fetchActive === null) {
        shouldFetchActive = state.routeVersionSource === 'active';
      }

      // Loop (RING) routes never fetch draft — their path comes from routeDetails.
      // Enforce at this entry point so every caller satisfies the "never fetch draft"
      // behavior, not just getInitialData.
      const isLoopRoute = Boolean(state.routeDetails?.ringId);
      if (isLoopRoute && !shouldFetchActive) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('fetchRouteVersion: skipping draft fetch for loop (RING) route', routeId);
        }
        commit('SET_CURRENT_VERSION_SOURCE', 'active');
        return null;
      }

      try {
        commit('SET_LOADING', true);

        const versionData = await routeListService.getRouteVersion(routeId, shouldFetchActive);
        if (shouldApply && !shouldApply()) return null;

        const versionType = shouldFetchActive ? 'active' : 'draft';
        const versionKey = shouldFetchActive ? 'activeVersion' : 'draftVersion';

        // Validate API response - check both that versionData exists and has the expected version key
        if (!versionData || typeof versionData !== 'object') {
          if (process.env.NODE_ENV === 'development') {
            console.warn(`No ${versionType} version data returned for route ${routeId}`);
          }
          return null;
        }

        // Check if the expected version key exists in the response
        if (!versionData[versionKey]) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              `${versionType} version data is missing the expected '${versionKey}' property for route ${routeId}`,
            );
          }
          // Ensure stations is always an array before storing
          const normalizedData = {
            ...versionData,
            stations: Array.isArray(versionData.stations) ? versionData.stations : [],
          };
          commit('SET_STORED_ROUTE_VERSION', { versionType, payload: normalizedData });
          return versionData;
        }

        // Ensure stations is always an array before storing
        const normalizedVersionData = {
          ...versionData,
          stations: Array.isArray(versionData.stations) ? versionData.stations : [],
        };

        // Store the normalized version data
        commit('SET_STORED_ROUTE_VERSION', { versionType, payload: normalizedVersionData });

        // CRITICAL: Always update currentSource to match what was actually fetched
        // This ensures prepareRouteData reads from the correct version
        // Previously only updated when fetchActive !== null, but this caused issues
        // when the API returned a different version type than expected
        commit('SET_CURRENT_VERSION_SOURCE', versionType);

        // Prepare data - we've already validated versionData[versionKey] exists
        // CRITICAL: Must await to ensure routesData is populated before cacheCurrentRouteData runs
        await dispatch('prepareRouteData');
        await dispatch('prepareStopData');

        return versionData;
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error(`Error fetching ${shouldFetchActive ? 'active' : 'draft'} version:`, error);
        }
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // DEPRECATED: Kept for backward compatibility
    // Use fetchRouteVersion instead
    async fetchDraftVersions({ commit, dispatch }, routeId) {
      try {
        commit('SET_LOADING', true);
        const draftVersions = await routeListService.getDraftRoutes(routeId);
        commit('SET_STORED_DRAFT_VERSION', draftVersions);

        // Only prepare data if we have valid draft versions
        if (draftVersions?.draftVersion) {
          await dispatch('prepareRouteData');
          await dispatch('prepareStopData');
        }

        return draftVersions;
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching draft versions:', error);
        }
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    prepareRouteData({ commit, state }) {
      const currentSource = state.routeVersionSource;

      // Loop (RING) routes cannot be edited and may have their geometry created
      // directly on the BE. Their path should come from the route-details endpoint
      // (`rest/v3/routes/{id}` → routePath.encodedPolyline) rather than a draft
      // version, so they never appear as draft routes. Detected via a truthy ringId.
      const isLoopRoute = Boolean(state.routeDetails?.ringId);

      if (isLoopRoute && state.routeDetails) {
        const ringRoutesData = mappedRouteDataFromRouteDetails(state.routeDetails);
        const safeRingRoutesData = ringRoutesData || { TO: {}, FROM: {} };

        commit('SET_ROUTES_ALL', safeRingRoutesData);

        if (safeRingRoutesData.TO?.stopIds?.length > 0) {
          state.isCalculated.TO = true;
        }
        if (safeRingRoutesData.FROM?.stopIds?.length > 0) {
          state.isCalculated.FROM = true;
        }
        return;
      }

      // Get version data based on current source
      let versionData;
      if (currentSource === 'active') {
        versionData = {
          activeVersion: state.storedRouteVersion.activeVersion,
          stations: state.storedRouteVersion.activeStations,
        };
      } else {
        versionData = {
          draftVersion: state.storedRouteVersion.draftVersion,
          stations: state.storedRouteVersion.draftStations,
        };
      }

      // Check if version data is available
      const hasVersionData = currentSource === 'active' ? versionData.activeVersion : versionData.draftVersion;

      if (!hasVersionData) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('No version data available for prepareRouteData');
        }
        // Ensure routesData structure exists even when no data
        commit('SET_ROUTES_ALL', { TO: {}, FROM: {} });
        return;
      }

      const newRoutesData = mappedRouteData(versionData);
      // Ensure we always have a valid structure
      const safeRoutesData = newRoutesData || { TO: {}, FROM: {} };

      commit('SET_ROUTES_ALL', safeRoutesData);

      // Use safeRoutesData consistently instead of newRoutesData
      if (safeRoutesData.TO?.stopIds?.length > 0) {
        state.isCalculated.TO = true;
      }
      if (safeRoutesData.FROM?.stopIds?.length > 0) {
        state.isCalculated.FROM = true;
      }
    },

    async calculateRouteAndStations({ commit, dispatch, state, rootState, rootGetters }, routeDirection = null) {
      const { isRouteToggleOn, shouldCalculateTime } = state;
      const { routeList } = rootState;
      const { currentWorkgroup } = routeList || {};
      const { listOfStations, currentWorkgroupCampuses, isSube } = rootGetters;

      const targetRoute = routeDirection || (isRouteToggleOn ? 'TO' : 'FROM');
      const template = currentWorkgroup?.template;

      if (!template) {
        return { error: true, message: 'Missing workgroup template' };
      }

      const isIncoming = template.toType === 'CAMPUS';
      const isInbound = (isIncoming && targetRoute === 'TO') || (!isIncoming && targetRoute === 'FROM');
      const stations = listOfStations(targetRoute);

      // WORKAROUND - Bu kontrol Garanti şubeleri için eklendi. Gereken geliştirme yapıldığı zaman burası da düzenlenecek
      if (isSube && stations.length < 2) {
        return { error: true, message: 'Need to add multiple stops for branch routes' };
      }
      commit('SET_CALCULATING_STATION_TIMES', true, { root: true });
      try {
        const campusesRaw = currentWorkgroupCampuses || [];
        if (!campusesRaw.length) {
          return { error: true, message: 'No campuses available for route calculation' };
        }

        const campuses = targetRoute === 'TO' ? [...campusesRaw] : [...campusesRaw].reverse();
        const campusTimezone = campuses[0]?.timezone;

        const stopDuration = (template.stopServiceTimeInMin || 0) * 60;
        const routeCoordinates = getRouteCoordinates({ stations, campuses, isInbound, isSube, stopDuration });

        const payload = getRouteTimeParams({
          template,
          isInbound,
          routeDirection: targetRoute,
          campusTimezone,
        });

        const r = await getDirectionsHere(routeCoordinates, payload, campusTimezone);
        const { sections } = r.routes[0] || {};
        const routeSummary = completeRouteSummary(sections);

        dispatch('updateRoutes', { route: targetRoute, routeSummary, sections });

        // Only calculate station times if shouldCalculateTime is true
        if (shouldCalculateTime) {
          await dispatch('calculateStationTime', {
            route: targetRoute,
            sections,
            serviceTimeForStopInSeconds: stopDuration,
            isInbound,
            campusCount: campuses.length,
          });
          // Hide warning when times are calculated
          commit('SET_SHOW_MISSING_TIMES_WARNING', false);
        } else {
          // Show warning when calculate is clicked without time calculation
          commit('SET_SHOW_MISSING_TIMES_WARNING', true);
        }

        state.isCalculated[targetRoute] = true;
        return { error: false, message: 'Calculation completed successfully' };
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error calculating route:', err);
        }
        return { error: true, message: 'Failed to calculate route' };
      } finally {
        commit('SET_CALCULATING_STATION_TIMES', false, { root: true });
      }
    },

    async updateRoutes({ commit }, { route, routeSummary, sections }) {
      commit('UPDATE_ROUTES', {
        route,
        payload: {
          path: routeSummary.path.map(([lat, lng]) => ({ lat, lng })),
          distanceInKm: Number((routeSummary.length / 1000).toFixed(2)),
          durationInMin: Number((routeSummary.duration / 60).toFixed()),
          legs: routeSummary.legs,
          encodedPolyline: routeSummary.encodedPolyline,
          sections,
        },
      });
    },

    // Recalculate one or more directions in sequence. Used by the interactive editor
    // handlers to auto-recompute a direction (path + station times) after a structural
    // change (add / mirror / reorder / remove) so the user does not have to click
    // Calculate. Each `calculateRouteAndStations` run uses the correct inbound/outbound
    // flag and campus timezone for its direction and, on success, re-flags it calculated.
    async recalculateDirections({ dispatch }, directions = ['TO', 'FROM']) {
      const dirs = Array.isArray(directions) && directions.length ? directions : ['TO', 'FROM'];
      const results = [];
      /* eslint-disable no-await-in-loop */
      for (let i = 0; i < dirs.length; i += 1) {
        results.push(await dispatch('calculateRouteAndStations', dirs[i]));
      }
      /* eslint-enable no-await-in-loop */
      return results;
    },

    async saveStationStats({ state, rootGetters }) {
      // Check draft version ID first to avoid unnecessary processing
      const draftVersionId = state.storedRouteVersion?.draftVersion?.id;
      if (!draftVersionId) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('No draft version ID found in storedRouteVersion - skipping station stats save');
        }
        return;
      }

      // Validate that station lists are arrays before processing
      const stationsTo = rootGetters.listOfStationsTo;
      const stationsFrom = rootGetters.listOfStationsFrom;

      if (!Array.isArray(stationsTo) || !Array.isArray(stationsFrom)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Station lists are not arrays:', { stationsTo, stationsFrom });
        }
        return;
      }

      const firstLegStations = stationsTo.map((stop) => mappedStationData(stop, 'FIRST_LEG'));
      const lastLegStations = stationsFrom.map((stop) => mappedStationData(stop, 'RETURN_LEG'));

      // Validate mapped results are arrays before spreading
      if (!Array.isArray(firstLegStations) || !Array.isArray(lastLegStations)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Mapped station data is not arrays:', { firstLegStations, lastLegStations });
        }
        return;
      }

      const statsData = {
        [draftVersionId]: [...firstLegStations, ...lastLegStations],
      };
      await routeListService.updateStationStats(statsData);
    },

    async saveRouteDetails({ commit, dispatch, state, rootState, rootGetters }) {
      // Use new storedRouteVersion structure
      const draftVersionData = {
        draftVersion: state.storedRouteVersion?.draftVersion,
        stations: state.storedRouteVersion?.draftStations,
      };

      const updatedRouteDetails = getUpdatedRouteDetails(
        draftVersionData || {},
        state.routesData.TO,
        state.routesData.FROM,
        rootGetters.listOfStations('TO'),
        rootGetters.listOfStations('FROM'),
        rootState.routeList.currentWorkgroup.template.direction === 'ROUND_TRIP',
      );

      commit('SET_LOADING', true);
      try {
        const routeId = state.storedRouteVersion?.draftVersion?.routeId || null;
        const draftRouteId = updatedRouteDetails?.routeId || null;

        // CRITICAL: Validate that routeId matches draftVersion.routeId
        if (routeId && draftRouteId && routeId !== draftRouteId) {
          const error = new Error(
            `Route ID mismatch: Cannot save draft. Expected routeId: ${routeId}, but draft has routeId: ${draftRouteId}. ` +
              `This usually means the draft version is stale or belongs to a different route.`,
          );
          if (process.env.NODE_ENV === 'development') {
            console.error('Route ID mismatch detected:', {
              expectedRouteId: routeId,
              draftRouteId,
              draftVersionId: state.storedRouteVersion?.draftVersion?.id,
              currentRouteId: rootState.routeShifts?.currentRouteId,
            });
          }
          throw error;
        }

        await routeListService.updateDraftRoutes(routeId, updatedRouteDetails);
        await dispatch('saveStationStats');
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error saving route details:', error);
        }
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    async activateRouteDetails({ commit, state, rootState }) {
      // Use new storedRouteVersion structure
      const routeId = state.storedRouteVersion?.draftVersion?.routeId || null;
      const draftId = { id: state.storedRouteVersion?.draftVersion?.id || null };

      if (!draftId.id || !routeId) {
        throw new Error('Draft ID or Route ID is missing');
      }

      // CRITICAL: Validate that we're activating the correct route
      const currentRouteId = rootState.routeShifts?.currentRouteId;
      if (currentRouteId && routeId !== currentRouteId) {
        const error = new Error(
          `Route ID mismatch: Cannot activate draft. Current route: ${currentRouteId}, but draft belongs to route: ${routeId}. ` +
            `This usually means the draft version is stale or belongs to a different route.`,
        );
        if (process.env.NODE_ENV === 'development') {
          console.error('Route ID mismatch detected during activation:', {
            currentRouteId,
            draftRouteId: routeId,
            draftVersionId: draftId.id,
          });
        }
        throw error;
      }

      try {
        commit('SET_LOADING', true);
        await routeListService.activateRoute(routeId, draftId);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error activating route details:', error);
        }
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
    updateSingleLegendItem({ commit }, { model, isShown }) {
      commit('SET_SINGLE_LEGEND_ITEM', { item: model, value: isShown });
    },
    updateLegendItems({ commit }, items) {
      commit('SET_LEGEND_ITEMS', items);
    },
    updateRouteDetails({ commit }, payload) {
      commit('SET_ROUTE_DETAILS', payload);
    },
    setRouteEditLoading({ commit }, payload) {
      commit('SET_LOADING', payload);
    },
    setDisplayFirstCalculationMessageReverseDirection({ commit, state }, payload) {
      const key = state.isRouteToggleOn ? ' FROM' : 'TO';
      commit('SET_DISPLAY_FIRST_CALCULATION_MESSAGE', { key, value: payload });
    },
    setDisplayMultiShiftsMessage({ commit }, payload) {
      commit('SET_DISPLAY_FIRST_CALCULATION_MESSAGE', { key: 'MULTI_SHIFTS', value: payload });
    },
  },

  getters: {
    routesByDirection: (state) => (route) => {
      if (!state.routesData || !state.routesData[route]) return {};
      return state.routesData[route];
    },
    routesOfCurrentTab(state) {
      const route = state.isRouteToggleOn ? 'TO' : 'FROM';
      if (!state.routesData || !state.routesData[route]) return {};
      return state.routesData[route];
    },
    personnelStationsOfRoutes: (state) => (route) => {
      if (!state.routesData || !state.routesData[route] || !state.routesData[route].personnelStations) return {};
      return state.routesData[route].personnelStations;
    },
    isStopsCopiedMessageVisible(state) {
      return state.displayFirstCalculationMessage[state.isRouteToggleOn ? 'TO' : 'FROM'];
    },
    routeEditActionMode(state) {
      return state.routeEditActionMode;
    },
    routeHistoryMarker(state) {
      return state.routeHistoryMarker;
    },
    storedRouteVersion(state) {
      return state.storedRouteVersion;
    },
    storedActiveVersion(state) {
      return {
        activeVersion: state.storedRouteVersion.activeVersion,
        stations: state.storedRouteVersion.activeStations,
      };
    },
    storedDraftVersionNew(state) {
      return {
        draftVersion: state.storedRouteVersion.draftVersion,
        stations: state.storedRouteVersion.draftStations,
      };
    },
    // Current version source getter
    currentVersionSource(state) {
      return state.routeVersionSource;
    },
    // Get current version data based on routeVersionSource
    currentVersionData(state) {
      const currentSource = state.routeVersionSource;
      if (currentSource === 'active') {
        return {
          version: state.storedRouteVersion.activeVersion,
          stations: state.storedRouteVersion.activeStations,
          type: 'active',
        };
      }
      return {
        version: state.storedRouteVersion.draftVersion,
        stations: state.storedRouteVersion.draftStations,
        type: 'draft',
      };
    },
    toOrFrom(state) {
      return state.isRouteToggleOn ? 'TO' : 'FROM';
    },
    isAssignPersonnelModeOn(state) {
      return state.routeEditActionMode === routeActions.ASSIGN_PERSONNEL;
    },
    routeId(state) {
      return state.storedRouteVersion?.draftVersion?.routeId;
    },
    routeShifts(state) {
      return state.routeDetails?.routeShifts;
    },
    isRouteLoading(state) {
      return state.isLoading;
    },
    responsibleCampus(state, getters, rootState) {
      const responsibleCampusId = rootState.routeList?.currentWorkgroup?.template?.responsibleCampusId;
      if (!responsibleCampusId) return null;

      const campus = rootState.campuses?.find((c) => c.id === responsibleCampusId);
      return campus || null;
    },
    stopsOnMap(state) {
      return state.stopsOnMap;
    },
    stopsById(state) {
      return state.stopsById;
    },
    stopsTruncated(state) {
      return state.stopsTruncated;
    },
    showStopsOnMap(state) {
      return state.showStopsOnMap;
    },
    showRestrictionsOnMap(state) {
      return state.showRestrictionsOnMap;
    },
    stopCategories(state) {
      return state.stopCategories;
    },
    // VSER-13369: loaded/indexed stops keyed by exact coordinates. Mirrors the BE
    // canonical-station rule so reverse-direction counter stations (new row, same
    // coordinates as the stop) still resolve to the stop's category.
    stopsByCoord(state) {
      const byCoord = {};
      Object.values(state.stopsById).forEach((stop) => {
        const key = coordKey(stop && stop.location);
        if (key) byCoord[key] = stop;
      });
      return byCoord;
    },
    // VSER-13369: resolved pin colour for a route station (§5.4). A stop IS a station
    // row on the BE (Add-to-Route reuses the canonical station), so match the indexed
    // stops by station id, then by exact coordinates, then by the in-session add link;
    // manually-created (non-stop) stations stay black.
    stationPinColor: (state, getters) => (station) => {
      if (!station) return '#000000';
      const byId = state.stopsById[station.id];
      if (byId) return categoryPinColor(byId.category) || '#000000';
      const byCoord = getters.stopsByCoord[coordKey(station.location)];
      if (byCoord) return categoryPinColor(byCoord.category) || '#000000';
      const sessionCategory = state.routeStationPoiById[station.id]?.category;
      if (sessionCategory) return categoryPinColor(sessionCategory) || '#000000';
      return '#000000';
    },
    // POI ids already added to the route this session (to hide them from the stop layer).
    addedPoiIds: (state) =>
      new Set(
        Object.values(state.routeStationPoiById)
          .map((v) => v && v.poiId)
          .filter((id) => id != null),
      ),
  },
};

/* eslint-enable no-param-reassign */
/* eslint-enable no-unused-vars */
